import { BeakerIcon, ChatBubbleLeftRightIcon, HomeIcon, MagnifyingGlassIcon, PencilSquareIcon, SparklesIcon } from '@heroicons/react/20/solid'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CommandPalette, type AsyncCommandSource, type CommandPaletteItem } from '../catalyst/command-palette'
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '../catalyst/navbar'
import { Sidebar, SidebarBody, SidebarFooter, SidebarHeader, SidebarHeading, SidebarItem, SidebarLabel, SidebarSection, SidebarSpacer } from '../catalyst/sidebar'
import { SidebarLayout } from '../catalyst/sidebar-layout'
import { useAuth } from '../lib/auth'
import { useFarms } from '../lib/farms'
import { searchFertilizers } from '../lib/fertilizers'
import { searchPesticides } from '../lib/pesticides'
import { AccountMenu } from './account-menu'

const brandClass = 'font-serif text-lg italic tracking-tight'

/** Where a palette result in each product group opens. */
const PRODUCT_PATHS: Record<string, string> = { Pesticides: '/pesticides', Fertilizers: '/fertilizers' }

export function Shell({ children, flush }: React.PropsWithChildren<{ flush?: boolean }>) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { farms } = useFarms()
  const { org } = useAuth()
  // Advisors read the record; everyone else can log to it.
  const canLog = org?.role !== 'AGRONOMIST'
  const [paletteOpen, setPaletteOpen] = useState(false)
  const isHome = pathname === '/'

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const paletteItems: CommandPaletteItem[] = farms.map((farm) => ({
    id: farm.id,
    label: farm.name,
    description: farm.timezone,
    group: 'Farms',
    icon: HomeIcon,
  }))

  // One remote source for both product catalogues: the palette groups results by each item's own
  // `group`. allSettled, so one catalogue failing never hides the other's results.
  const productSource: AsyncCommandSource = {
    group: 'Products',
    search: async (query) => {
      const [pesticides, fertilizers] = await Promise.allSettled([searchPesticides(query, 5), searchFertilizers(query, 5)])
      return [
        ...(pesticides.status === 'fulfilled'
          ? pesticides.value.map((pesticide) => ({
              id: pesticide.id,
              label: pesticide.productNameEn,
              description: pesticide.activeIngredientsRawEn ?? undefined,
              group: 'Pesticides',
              icon: BeakerIcon,
            }))
          : []),
        ...(fertilizers.status === 'fulfilled'
          ? fertilizers.value.map((fertilizer) => ({
              id: fertilizer.id,
              label: fertilizer.name,
              description: fertilizer.analysisRaw ?? fertilizer.supplier,
              group: 'Fertilizers',
              icon: SparklesIcon,
            }))
          : []),
      ]
    },
  }

  const sidebarContent = (
    <Sidebar>
      <SidebarHeader>
        <span className={`flex items-center px-2 py-2 text-zinc-950 dark:text-white ${brandClass}`}>Farmula</span>
      </SidebarHeader>

      <SidebarBody>
        <SidebarSection>
          <SidebarItem onClick={() => setPaletteOpen(true)}>
            <MagnifyingGlassIcon className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
            <SidebarLabel>Search</SidebarLabel>
            <span className="ml-auto text-xs font-medium text-zinc-400 dark:text-zinc-500">⌘K</span>
          </SidebarItem>
        </SidebarSection>

        <SidebarSection>
          <SidebarHeading>Farms</SidebarHeading>
          <SidebarItem href="/" current={isHome}>
            <HomeIcon className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
            <SidebarLabel>Farms</SidebarLabel>
          </SidebarItem>
        </SidebarSection>

        {canLog && (
          <SidebarSection>
            <SidebarHeading>Record</SidebarHeading>
            <SidebarItem href="/log" current={pathname === '/log' || pathname.startsWith('/log/') || /^\/farms\/[^/]+\/log(\/|$)/.test(pathname)}>
              <PencilSquareIcon className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
              <SidebarLabel>Log activity</SidebarLabel>
            </SidebarItem>
          </SidebarSection>
        )}

        <SidebarSection>
          <SidebarHeading>Products</SidebarHeading>
          <SidebarItem href="/pesticides" current={pathname.startsWith('/pesticides')}>
            <BeakerIcon className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
            <SidebarLabel>Pesticides</SidebarLabel>
          </SidebarItem>
          <SidebarItem href="/fertilizers" current={pathname.startsWith('/fertilizers')}>
            <SparklesIcon className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
            <SidebarLabel>Fertilizers</SidebarLabel>
          </SidebarItem>
        </SidebarSection>

        <SidebarSection>
          <SidebarHeading>Ask</SidebarHeading>
          <SidebarItem href="/ask" current={pathname.startsWith('/ask')}>
            <ChatBubbleLeftRightIcon className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
            <SidebarLabel>Ask Farmula</SidebarLabel>
          </SidebarItem>
        </SidebarSection>
        <SidebarSpacer />
      </SidebarBody>

      <SidebarFooter className="max-lg:hidden">
        <AccountMenu variant="sidebar" />
      </SidebarFooter>
    </Sidebar>
  )

  const mobileNavbar = (
    <Navbar>
      <NavbarItem href="/" current={isHome} className={brandClass}>
        Farmula
      </NavbarItem>
      <NavbarSpacer />
      <NavbarSection>
        {canLog && (
          <NavbarItem href="/log" aria-label="Log activity" current={pathname === '/log' || pathname.startsWith('/log/')}>
            <PencilSquareIcon />
          </NavbarItem>
        )}
        <NavbarItem aria-label="Search" onClick={() => setPaletteOpen(true)}>
          <MagnifyingGlassIcon />
        </NavbarItem>
        <AccountMenu variant="navbar" />
      </NavbarSection>
    </Navbar>
  )

  return (
    <SidebarLayout navbar={mobileNavbar} sidebar={sidebarContent} flush={flush}>
      {children}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        items={paletteItems}
        asyncSource={productSource}
        onSelect={(item) => navigate(PRODUCT_PATHS[item.group] ? `${PRODUCT_PATHS[item.group]}/${item.id}` : `/farms/${item.id}`)}
        placeholder="Search farms, pesticides or fertilizers…"
        prompt="Search Farmula"
        promptDescription="Find a farm by name, a pesticide by name or active ingredient, or a fertilizer by name, brand or supplier."
      />
    </SidebarLayout>
  )
}
